// ITEM 15 (her `13`) — **THE NIGHTLY HEALTH PATROL, AND THE ONE LINE SHE READS IN THE MORNING.**
//
// Her order: a scheduled SERVER-SIDE nightly check — generations and failures, error classes,
// vendor meters and cache rates, Stripe reconciliation, fast checks against production — **a morning
// health line on Overview in PLAIN LANGUAGE**, **reds auto-filing to the ticket sheet**, and **ZERO
// PAID ENGINE CALLS.**
//
// THIS FILE IS THE LAW AND HOLDS NO READERS. It is imported by the server (which takes the
// measurements) and by the dashboard (which draws the line), so **what a patrol check IS, what it is
// called in her words, and whether a red belongs on her IT sheet all have exactly one answer**
// (R28/R29). **It imports only `./alertDoors.js`, which itself imports NOTHING** — so a FAST
// fixture still loads and RUNS this with no browser, no database and no network (R146: the reach
// is transitive, so what matters is the whole chain and not this file's own import list). That
// line read *"It imports NOTHING"* until M1 and is corrected here rather than left true-sounding.
//
import { alertDoor, alertRendersAsDoor, doorQuery } from "./alertDoors.js";
//
// **THE HARDEST CONSTRAINT IN THE ITEM IS THE PLAIN-LANGUAGE ONE AND IT IS WHY THIS ROSTER EXISTS.**
// ITEM A's `plainLanguageViolations` refuses a machine key, a ruling code and an underscore anywhere
// outside Advanced — and a patrol's entire raw material is machine keys (`gate_log`, `line-refused`,
// `chart_failed`). Every check therefore carries the sentence she reads, declared beside the key it
// is a translation OF, rather than a screen composing one from the key at render time.

/**
 * **WHAT A CHECK CAN ANSWER, AND THERE ARE THREE OF THEM (R18/R65).**
 *
 *   `ok`      — measured, and nothing is wrong.
 *   `red`     — measured, and something is wrong.
 *   `unknown` — **the measurement itself did not happen.** Not a zero, not a pass.
 *
 * Collapsing `unknown` into `ok` is the single most expensive thing this file could do: it puts
 * *"everything is healthy"* on the screen of an owner whose patrol could not reach the database, and
 * she stops looking. That is R65's finding on this exact page and it is why `NEEDS_YOU_UNREAD`
 * already exists as its own sentence beside `NEEDS_YOU_CLEAR`.
 */
export const PATROL_STATES = ["ok", "red", "unknown"];

/**
 * **WHICH REDS BELONG ON HER IT SHEET, AND WHY THAT IS NOT ALL OF THEM.**
 *
 * The sheet's own CC header is *"These are the IT issues for {day} … All of these need to be
 * fixed."* — so it is a WORK ORDER. A quiet revenue day and a low cache hit rate are facts about the
 * business, not broken things, and filing them there hands CC a work order about something nobody
 * can fix. `files` is the flag, declared per check rather than decided at the filing site.
 *
 * **AND `unknown` FILES TOO, WHICH IS THE NON-OBVIOUS HALF.** A check that could not run is a check
 * nobody is doing, and the whole reason this patrol exists is that nothing was watching. Silence is
 * the failure mode her item is about.
 */
export const PATROL_CHECKS = [
  {
    key: "failures",
    say: "Readings that failed",
    files: true,
    // The window is 24 hours and is stated on the line, because a patrol that summed a range would
    // be answering a different question from the one she asked at breakfast.
    note: "How many readings could not be delivered overnight, and to how many members.",
  },
  {
    key: "errors",
    say: "Errors behind the gates",
    files: true,
    note: "Anything the gates recorded as an error rather than a refusal.",
  },
  {
    key: "money",
    say: "Money that does not add up",
    files: true,
    note: "The takings this house has recorded, read back against Stripe itself.",
  },
  {
    key: "production",
    say: "The live site",
    files: true,
    note: "Whether the site answers, and which build it is running.",
  },
  {
    key: "cache",
    say: "Work being repeated",
    // NOT on her sheet, and declared with its reason: a low reuse rate is a cost fact she may want to
    // act on and is not something broken. Filing it would put a work order on CC's desk that has no
    // fix at the end of it.
    files: false,
    note: "How much outside work is being reused instead of bought again.",
  },
  {
    key: "vendors",
    say: "Outside services",
    files: false,
    // **THE WINDOW IS NAMED ON THE LINE ITSELF, AND THAT IS A RULING RATHER THAN A CAPTION.** The
    // vendor meter is in-process memory: everything in it is since this container started, and
    // Railway restarts on every deploy. A figure printed as though it covered last night would be a
    // number she cannot act on and cannot check (R18: two facts, said as one).
    note: "How the outside services answered, counted since this server last started.",
  },
  {
    // >>> **VENDOR-CREDIT-1 (2026-09-04) — THE ACCOUNT, WHICH IS A DIFFERENT FACT FROM THE SERVICE.**
    // <<< `vendors` above counts calls that FAILED; this one asks whether they failed because the
    // house has no money with the vendor. On 2026-09-03, 17:20-17:48, that refused 72 turns for 22
    // members in 28 minutes and nothing on any screen said so — the incident was found by hand, days
    // later, by grepping a jsonb blob (R18: an outage and an empty account are two mornings, and only
    // one of them clears by waiting).
    key: "credit",
    say: "The account behind Lala",
    // **NOT ON CC's WORK ORDER, AND THE REASON IS THE WHOLE POINT.** A ticket is a thing somebody can
    // fix, and nobody here can fix this one: the remedy is the OWNER's card. Filing it would put a
    // work order on a desk that has no action at the end of it, which is exactly why `cache` and
    // `vendors` are declared `false` two entries up. She still reads the line — `files` decides the
    // ticket, never the screen.
    files: false,
    note: "Whether Lala was refused for want of credit with the outside model, in the last day.",
  },
];

export const PATROL_KEYS = PATROL_CHECKS.map((c) => c.key);
export const patrolCheck = (key) => PATROL_CHECKS.find((c) => c.key === String(key || "")) || null;

/** Does a red on this check belong on the work order she hands to CC? An unrostered key does not. */
export function patrolFiles(key) {
  const c = patrolCheck(key);
  return Boolean(c && c.files);
}

/** What she reads instead of the key. An unrostered key gets a sentence rather than a blank. */
export function patrolSentence(key) {
  const c = patrolCheck(key);
  return c ? c.say : "Something this house has no name for yet";
}

// ---------------------------------------------------------------------------
// **ONCE PER PACIFIC NIGHT, REGARDLESS OF SERVER BOOTS** (her ruling, 2026-08-12, A1).
//
// **THE DEFECT THIS LAW EXISTS FOR, MEASURED BEFORE A LINE WAS WRITTEN (R25/R180):
// `health_patrol` held 85 RUNS IN 31 HOURS.** `startHealthPatrol` ticks hourly AND calls `run()`
// once more on every boot, and `makeMorningRunner` guards only *in flight* — it has no concept of
// *already ran today*. So every walkthrough, every dev server and every deploy added a pass, and
// each pass with a red filed a ticket: **81 patrol tickets against 2 real member tickets on her IT
// sheet.** The flood was never the ticket code. It was the schedule.
//
// **THE GUARD IS THE STORE'S OWN LAST DAY KEY AND NOT A VARIABLE IN THIS PROCESS, AND THAT IS THE
// WHOLE ROUND.** A module-scope `let ranToday` is reset by the very thing being guarded against —
// a boot — so it would read perfectly, pass every presence check, and change nothing at all.
//
// **THREE STATES, NEVER TWO (R18), because they want different acts from the caller:** `ran-today`
// (tonight's pass is done — do nothing, quietly), `too-early` (the day has turned but the night
// hour has not arrived — wait), `due` (run it). Collapsing the first two makes a skipped tick
// indistinguishable from a satisfied one in the log, which is the fact `tick-skipped` exists to
// carry.
//
// **A DAY WITH NO RUN AT ALL STILL RUNS LATE RATHER THAN NEVER.** If the house was down all night
// and boots at 09:00, `lastDayKey` is yesterday's and the hour is past the floor, so it is `due`.
// A patrol that refuses to run because it missed its window is the silence this whole item exists
// to end (ITEM 15's own sentence).
// ---------------------------------------------------------------------------

/** The Pacific hour the night check may first run. 2am: past midnight, before she is awake. */
export const PATROL_HOUR = 2;

export const PATROL_DUE = "due";
export const PATROL_RAN_TODAY = "ran-today";
export const PATROL_TOO_EARLY = "too-early";

/**
 * May tonight's pass run?
 *
 * @param dayKey     the house's CURRENT Pacific day key (`localPartsFor(now, HOUSE_ZONE)`)
 * @param hour       the house's current Pacific hour, 0-23
 * @param lastDayKey the day key of the newest row in `health_patrol` — **read from the store**, so a
 *                   restart cannot forget it. An empty/absent value means the patrol has never run.
 */
export function patrolDue({ dayKey, hour, lastDayKey } = {}) {
  const day = String(dayKey || "");
  // An unreadable clock may not be read as "go" — a patrol that runs on every tick is the defect
  // this law replaces, so an unusable day key waits rather than firing (R18: fail closed on the
  // side that costs a delay rather than the side that costs a flood).
  if (!day) return PATROL_TOO_EARLY;
  if (String(lastDayKey || "") === day) return PATROL_RAN_TODAY;
  // `Number(true)` is 1 and `Number("2")` is 2, so an hour that is not a real number is refused
  // rather than coerced into a legal-looking one (R136/R137).
  const h = typeof hour === "number" && Number.isFinite(hour) ? Math.trunc(hour) : -1;
  if (h < 0 || h > 23) return PATROL_TOO_EARLY;
  return h >= PATROL_HOUR ? PATROL_DUE : PATROL_TOO_EARLY;
}

// ---------------------------------------------------------------------------
// THE MORNING LINE. **THREE STATES, NOT TWO**, for the reason declared at the top of this file.
// ---------------------------------------------------------------------------
export const PATROL_UNREAD = "Last night's check could not be read just now.";
export const PATROL_NEVER_RAN = "The nightly check has not run yet.";
export const PATROL_CLEAR = "Last night's check found nothing wrong.";

// >>> M1 — **`PATROL_TO` IS DELETED AT ITS DECLARATION (DL-2/DT-2b/R181), NOT OVERRIDDEN BELOW IT.**
// <<< It read `export const PATROL_TO = "tickets"` — **ONE destination for all six checks**, so
// every row on this line was a tappable box landing on the same generic list, `ok` rows included.
// Two of the six (`cache`, `vendors`) are `files: false` by their own ruling directly above, which
// means the list they opened **could never contain a row about them**: her own *"Outside services"*
// screenshot, and the purest form of the dead-door disease this house has paid for three times.
//
// A shared constant is not a destination, it is the ABSENCE of one — which is why nothing could go
// stale and nothing ever went red. Each check names its own door in `src/alertDoors.js` now, and a
// check with no actionable detail behind it declares `door: null` **with its reason** and is drawn
// as a reading rather than as a promise (her clause 4).
//
// A retired constant that still resolves is one a later round reads as the answer, so it is gone
// rather than pointed somewhere better.

/**
 * The ONE sentence on her Overview.
 *
 *   `undefined` — the payload has not arrived. **Holds still**: returns null and the screen draws
 *                 nothing, exactly as the needs-you list does.
 *   `null`      — the payload arrived and the patrol could not be read. Its own sentence.
 *   `{ ranAt: null }` — the patrol has never run. **A different fact from a clean night**, and the
 *                 one this whole item exists to make visible: silence is not health.
 *   an answer   — the count, in her words.
 */
export function patrolLine(patrol) {
  if (patrol === undefined) return null;
  if (patrol === null || typeof patrol !== "object") return PATROL_UNREAD;
  if (!patrol.ranAt) return PATROL_NEVER_RAN;
  const reds = Math.max(0, Math.trunc(Number(patrol.reds) || 0));
  const unknowns = Math.max(0, Math.trunc(Number(patrol.unknowns) || 0));
  if (!reds && !unknowns) return PATROL_CLEAR;
  const parts = [];
  if (reds) parts.push(`${reds} ${reds === 1 ? "thing" : "things"} to look at`);
  // **A CHECK THAT COULD NOT RUN IS NAMED SEPARATELY AND IS NEVER ADDED TO THE REDS.** They are
  // different facts and only one of them is a defect she can act on; summing them would tell her a
  // database blip was a broken generator.
  if (unknowns) parts.push(`${unknowns} ${unknowns === 1 ? "check" : "checks"} that could not run`);
  return `Last night's check found ${parts.join(" and ")}.`;
}

/**
 * The rows under the line, each already in her words. Null while the payload is in flight, for the
 * same reason `patrolLine` returns null — the screen draws nothing rather than an empty table that
 * reads as a clean night.
 */
export function patrolRows(patrol) {
  if (!patrol || typeof patrol !== "object" || !Array.isArray(patrol.checks)) return null;
  const order = { red: 0, unknown: 1, ok: 2 };
  return patrol.checks
    .filter((c) => c && PATROL_KEYS.includes(String(c.key)))
    .map((c) => {
      const key = String(c.key);
      const state = PATROL_STATES.includes(String(c.state)) ? String(c.state) : "unknown";
      const row = {
        key,
        say: patrolSentence(key),
        state,
        // Whatever the measurement wants to say in her words. Never a key, never a count with no noun.
        note: String(c.note || ""),
      };
      // M1 — **THE ROW CARRIES ITS OWN DOOR, OR IT CARRIES NONE.** Two facts, and the surface may
      // not compose them itself (R18): `door` is where this check's items live, and
      // `opensAsDoor` is whether this ROW, in THIS state, is an alert at all. An `ok` row is a
      // health readout and was being drawn as a tappable box; a red row with no detail behind it is
      // the thing her clause 4 refuses. Both are decided here, where the law can be RUN, rather
      // than in a ternary inside a TSX file no gate can reach (R8/R95).
      // **ASKED WITH THIS ROSTER'S OWN SCOPE**, because `money` names two different conditions:
      // here it is *the takings do not reconcile to Stripe*, and on her Overview list it is *money
      // questions in her pile*. Unscoped, a red reconciliation would send her to the Drafts view.
      const door = alertDoor(key, "patrol");
      row.door = door ? door.view : "";
      row.doorQuery = door ? doorQuery(door) : "";
      row.items = door ? door.items : "";
      // The door RESOLVED one line up, never a second lookup — see `alertRendersAsDoor`'s own note.
      row.opensAsDoor = alertRendersAsDoor(row, door);
      return row;
    })
    .sort((a, b) => (order[a.state] ?? 3) - (order[b.state] ?? 3) || a.key.localeCompare(b.key));
}

/**
 * **WHAT GOES ON THE WORK ORDER, AND IT IS ONE TICKET FOR THE NIGHT RATHER THAN ONE PER CHECK.**
 *
 * Her sheet is grouped into her own Pacific days and handed to CC as one list. Four reds on one bad
 * night filed as four tickets is four rows describing one outage, and the sheet stops being
 * readable exactly when it matters most. Returns null when nothing on it would be a work order —
 * so a night whose only red is the cache rate files NOTHING, which is `files: false` doing its job.
 */
export function patrolTicket(patrol) {
  const rows = patrolRows(patrol);
  if (!rows) return null;
  const filed = rows.filter((r) => r.state !== "ok" && patrolFiles(r.key));
  if (!filed.length) return null;
  const issue = filed
    .map((r) => `${r.say}: ${r.state === "unknown" ? "this check could not run" : (r.note || "something is wrong")}`)
    .join(" — ");
  return {
    issue: `The nightly check found ${filed.length} ${filed.length === 1 ? "thing" : "things"}. ${issue}`,
    keys: filed.map((r) => r.key),
  };
}
